import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTheme, useSetShopId, defaultTheme, useFlowPortalContent } from '../../context/PortalThemeContext';
import BackToStoreButton from './BackToStoreButton';
import './OrderLookup.css'
import { fetchDynamicOrder } from '../../utils/orderLookupApi';
import { fallbackString } from './FallbackUtils';
import ReturnPolicyLink from '../returns/ReturnPolicyLink';

const OrderLookup = ({ onOrderFound, onCustomerFound, onTrackingInfoFound, onProductDetailsFound, flowType = 'RETURN' }) => {
  const [additionalParam, setAdditionalParam] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const theme = useTheme();
  
  // Ensure defaultSettings has all required properties with fallbacks
  const defaultSettings = {
    headingText: 'Returns & Exchanges',
    subheadingText: "First, let's look up your order.",
    additionalInfoPlaceholder: 'Email or Zip or Phone #',
    orderNumberPlaceholder: 'Order # (emailed to you)',
    ...defaultTheme?.flowPortalContentSettings?.[flowType]?.orderLookup
  };
  
  // Use the new flow-specific hook
  const flowPortalContent = useFlowPortalContent(flowType);
  const orderLookupSettings = flowPortalContent?.orderLookup || {};
  const setShopId = useSetShopId();

  // Debug logging
  useEffect(() => {
    // console.log('OrderLookup: defaultSettings loaded:', defaultSettings);
    // console.log('OrderLookup: orderLookupSettings loaded:', orderLookupSettings);
    // console.log('OrderLookup: flowPortalContent loaded:', flowPortalContent);
  }, [defaultSettings, orderLookupSettings, flowPortalContent]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle URL parameters for pre-filled lookup
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlOrderNumber = urlParams.get('orderNumber');
    const urlEmail = urlParams.get('email');
    const autoSearch = urlParams.get('autoSearch') === 'true';

    // console.log('OrderLookup: URL parameters detected:', { urlOrderNumber, urlEmail, autoSearch });

    if (urlOrderNumber) {
      setOrderNumber(urlOrderNumber);
    }
    if (urlEmail) {
      setAdditionalParam(urlEmail);
    }
  }, []);

  // Separate effect to handle auto-search when state is ready
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const autoSearch = urlParams.get('autoSearch') === 'true';
    
    if (autoSearch && orderNumber && additionalParam) {
      // console.log('OrderLookup: Auto-submitting with ready state:', { orderNumber, additionalParam });
      checkOrder();
    }
  }, [orderNumber, additionalParam]);

  // Safety check - don't render until we have the required data
  if (!theme || !defaultSettings || Object.keys(defaultSettings).length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div style={{ fontSize: '16px', color: 'var(--text-light)' }}>Loading...</div>
      </div>
    );
  }

  const checkOrder = async () => {
    setIsLoading(true);
    setError(null);

    const shopId = theme.store;

    try {
      const response = await fetchDynamicOrder({
        companyId: shopId,
        searchParam: orderNumber,
        additionalParam,
      });

      if (!response.ok) {
        setError('Order not found. Please check your details.');
        setIsLoading(false);
        return;
      }

      const ordersJson = await response.json();
      const order = ordersJson.order;

      if (!order) {
        setError('No order found.');
        setIsLoading(false);
        return;
      }

      if (!order.customer && order?.customer) {
        order.customer = order.customer;
      }

      if (ordersJson.bearerToken && order.order_number) {
        localStorage.setItem('sessionToken', ordersJson.bearerToken);
        localStorage.setItem('orderId', order.order_number);
        localStorage.setItem('shopId', shopId);
        localStorage.setItem('userEmail', additionalParam); // Store email for reauthentication
      }

      order.orderReturns = ordersJson.orderReturns;

      order.orderClaims = ordersJson.orderClaims;

      // Only call if we now have a valid customer
      if (order.customer?.email) {
        // console.log('OrderLookup: Calling onCustomerFound with:', order.customer);
        onCustomerFound(order.customer);
      }

      if (!order.orderNumber) order.orderNumber = orderNumber;

      setShopId(order.shopId);
      onOrderFound(order);
      onTrackingInfoFound(ordersJson.trackingInfo);
      onProductDetailsFound(ordersJson.shopifyProducts);

      clearQueryParams();
      
      // Simple refresh to ensure proper component transition
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (err) {
      console.error('checkOrder error:', err);
      setError('Something went wrong. Please try again.');
    }

    setIsLoading(false);
  };

  // Function to clear URL query parameters
  const clearQueryParams = () => {
    const url = new URL(window.location);
    url.search = '';
    window.history.replaceState({}, document.title, url.pathname);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    checkOrder();
  };

  return (
    <>
      <BackToStoreButton />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >

        {theme.imageSrc && (
          <div style={{ textAlign: 'center', marginBottom: '1.5rem', maxWidth: '100%' }}>
            <img
              src={theme.imageSrc}
              alt="logo"
              style={{ maxWidth: '180px', height: 'auto', width: '100%' }}
            />
          </div>
        )}

        <div className="order-lookup-card mt-3">
          <h2
            style={{
              fontSize: 'clamp(1.5rem, 5vw, 2rem)',
              fontWeight: 700,
              textAlign: 'center',
              color: theme.textDark,
              fontFamily: theme.headingFont,
              marginBottom: '2rem',
              wordBreak: 'break-word'
            }}
          >
            {fallbackString(orderLookupSettings?.headingText, defaultSettings?.headingText) || 'Returns & Exchanges'}
          </h2>
          <div className='text-center mb-4 w-100' style={{
            color: theme.textDark,
            fontSize: '1.125rem',
            fontWeight: 400
          }}>
            {fallbackString(orderLookupSettings?.subheadingText, defaultSettings?.subheadingText) || "First, let's look up your order."}
          </div>

          {error && (
            <Alert variant="danger" style={{ textAlign: 'center', fontWeight: 500 }}>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <div className="floating-label-group">
                <Form.Control
                  type="text"
                  placeholder=" "
                  value={additionalParam}
                  onChange={(e) => setAdditionalParam(e.target.value)}
                  required
                />
                <label>
                  {fallbackString(orderLookupSettings?.additionalInfoPlaceholder, defaultSettings?.additionalInfoPlaceholder) || 'Email or Zip or Phone #'}
                </label>
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <div className="floating-label-group">
                <Form.Control
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder=" "
                  required
                />
                <label>
                  {fallbackString(orderLookupSettings?.orderNumberPlaceholder, defaultSettings?.orderNumberPlaceholder) || 'Order # (emailed to you)'}
                </label>
              </div>
            </Form.Group>

            <Button
              type="submit"
              className="button-accent w-100"
              disabled={isLoading}
              style={{
                fontWeight: 700,
                fontSize: '20px',
                padding: '0.65rem',
                borderRadius: '8px',
                marginTop: '1rem',
                backgroundColor: theme.accentColor,
              }}
            >
              {isLoading ? <Spinner animation="border" size="sm" /> : 'Find Order'}
            </Button>
          </Form>

        </div>
        <ReturnPolicyLink addShadow={true} flowType={flowType} />
      </div>
    </>
  );
};

export default OrderLookup;
